// ─────────────────────────────────────────────
//  AicHandoffModal — the day must always have an AIC. Whenever a BOARD flow
//  sends the CURRENT AIC home (Assign → Home, Home-box ✓ Confirm home, MD-call
//  Confirm home), this modal interposes: pick the successor from the available
//  doctors, and only then does the departure proceed.
//
//  The successor inherits EVERYTHING automatically: AuthContext derives the
//  active-AIC role from daily_boards.aicId (roster-driven), so board powers,
//  break approvals, and the cross-facility "All AICs" chat channel all follow
//  the aicId write — no per-user flag to flip.
//
//  Uses the shared Modal (portals to <body>, so it works inside TV mode's
//  scaled board).
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
// ─────────────────────────────────────────────
import React, { useState } from "react";
import Modal from "./Modal";
import Button from "./Button";
import tokens from "../theme/tokens";

const { color, font, radius } = tokens;

export default function AicHandoffModal({ person, doctors = [], onConfirm, onClose, busy = false }) {
  const [successorId, setSuccessorId] = useState("");
  if (!person) return null;
  // Successor pool: every available doctor except the departing AIC.
  const pool = doctors.filter((d) => d && d.id && d.id !== person.id);
  const successor = pool.find((d) => d.id === successorId) || null;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Hand off AIC before sending home"
      maxWidth={440}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={() => successor && onConfirm(successor)} disabled={busy || !successor}>
            {busy ? "Handing off…" : "Hand off & send home"}
          </Button>
        </>
      }
    >
      <p style={st.note}>
        <strong>{person.name}</strong> is the AIC of the day. Choose who takes over —
        the new AIC gets full AIC control immediately, including the All-AICs chat
        with the other facilities.
      </p>
      <label style={st.label} htmlFor="aic-successor-select">New AIC</label>
      <select
        id="aic-successor-select"
        style={st.select}
        value={successorId}
        onChange={(e) => setSuccessorId(e.target.value)}
        disabled={busy}
      >
        <option value="">Select a doctor…</option>
        {pool.map((d) => (
          <option key={d.id} value={d.id}>{d.name}</option>
        ))}
      </select>
      {pool.length === 0 && (
        <p style={{ ...st.note, color: color.accent.error }}>
          No other doctors are available today — add a doctor to the roster first.
        </p>
      )}
    </Modal>
  );
}

const st = {
  note: { fontSize: 13, color: color.text.secondary, lineHeight: 1.5, marginBottom: 12 },
  label: { display: "block", fontSize: font.size.label, fontWeight: font.weight.bold, color: color.text.secondary, marginBottom: 6 },
  select: { width: "100%", boxSizing: "border-box", background: color.surface.card, border: `1px solid ${color.border.default}`, color: color.text.primary, borderRadius: radius.sm, padding: "9px 12px", fontSize: 13, fontFamily: font.family },
};
